import React, { useState, useEffect, useRef } from "react";
import ChatIcon from "@mui/icons-material/Chat";
import "../Styles/chatbot.css";

function Chatbot() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [step, setStep] = useState(0);
    const [attempts, setAttempts] = useState(0); 
    const [blockedUntil, setBlockedUntil] = useState(null);
    const [timer, setTimer] = useState(0);
    const [userData, setUserData] = useState({ name: "", issue: "", userCode: "", email: "", phone: "" });
    const chatEndRef = useRef(null);


    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
        const blockedTime = localStorage.getItem("blockedUntil");
        if (blockedTime) {
            const currentTime = Date.now();
            const remainingTime = parseInt(blockedTime) - currentTime;
            if (remainingTime > 0) {
                setBlockedUntil(parseInt(blockedTime));
                startTimer(remainingTime);
            } else {
                localStorage.removeItem("blockedUntil");
            }
        }
    }, []);


    const startTimer = (remainingTime) => {
        let seconds = Math.floor(remainingTime / 1000);
        setTimer(seconds);
        const interval = setInterval(() => {
            seconds -= 1;
            setTimer(seconds);
            if (seconds <= 0) {
                clearInterval(interval);
                setBlockedUntil(null);
                localStorage.removeItem("blockedUntil");
                addBotMessage("You can now enter your user code again.");
            }
        }, 1000);
    };



    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen && messages.length === 0) {
            addBotMessage("Hey, I'm UNIUMS Chat Bot!");
            setTimeout(() => addBotMessage("How can I assist you?"), 1000);
            setStep(1);
        }
    };

    const addBotMessage = (text) => {
        setMessages((prev) => [...prev, { text, sender: "bot" }]);
    };

    const sendMessage = () => {
        if (input.trim() === "") return;
        setMessages((prev) => [...prev, { text: input, sender: "user" }]);
        handleBotResponse(input);
        setInput("");
    };

    const handleBotResponse = async (text) => {
        if (step === 1) {
            addBotMessage("What is your name?");
            setStep(2);
        } else if (step === 2) {
            if (/^[a-zA-Z\s]+$/.test(text.trim())) {
                setUserData((prev) => ({ ...prev, name: text }));
                addBotMessage(`${text}, Please Enter your issue or problem.`);
                setStep(3);
            } else {
                addBotMessage("Please enter a valid name.");
            }
        } else if (step === 3) {
            setUserData((prev) => ({ ...prev, issue: text }));
            addBotMessage("Enter your user code.");
            setStep(4);
        } else if (step === 4) {
            verifyEmployeeCode(text);
        } else if (step === 5) {
            if (/^\d{10}$/.test(text.trim())) {
                setUserData((prev) => ({ ...prev, phone: text }));
                addBotMessage("Your details are almost complete. Now, enter your email ID.");
                setStep(6);
            } else {
                addBotMessage("Please enter a valid 10-digit phone number.");
            }
        } else if (step === 6) {
            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
                setUserData((prev) => ({ ...prev, email: text }));
                addBotMessage("Here are your details:");
                setTimeout(() => addBotMessage(`Name: ${userData.name}`), 500);
                setTimeout(() => addBotMessage(`Issue: ${userData.issue}`), 1000);
                setTimeout(() => addBotMessage(`User Code: ${userData.userCode}`), 1500);
                setTimeout(() => addBotMessage(`Email: ${text}`), 2000);
                setTimeout(() => addBotMessage(`Phone: ${userData.phone}`), 2500);
                setTimeout(() => addBotMessage("Can you submit this information? (yes/no)"), 3000);
                setStep(7);
            } else {
                addBotMessage("Please enter a valid email address.");
            }
        } else if (step === 7) {
            if (text.toLowerCase() === "yes") {
                addBotMessage("Submitting your details...");
                try {
                    const response = await fetch("https://api.uniums.com/api/send-email", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(userData),
                    });

                    const data = await response.json();

                    if (response.ok) {
                        addBotMessage("Your details have been submitted to the Support Team. Thank you!");
                    } else {
                        addBotMessage(`Failed to submit details: ${data.error}`);
                    }
                } catch (error) {
                    console.error("Error submitting details:", error);
                    addBotMessage("An error occurred while submitting your details.");
                }
            } else {
                addBotMessage("Restarting... Please enter your details again.");
                setMessages([]);
                setUserData({ name: "", issue: "", userCode: "", email: "" });
                setTimeout(() => toggleChat(), 1000);
            }
        }
    };

    const verifyEmployeeCode = async (code) => {
        if (blockedUntil) return;

        try {
            const response = await fetch("https://api.uniums.com/api/verify-employee-code", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ employeeCode: code }),
            });

            const data = await response.json();

            if (data.success) {
                setUserData((prev) => ({ ...prev, userCode: code }));
                addBotMessage(`Employee code verified. Welcome, ${data.user.name}! Please enter your phone number.`);
                setStep(5);
                setAttempts(0);
            } else {
                const newAttempts = attempts + 1;
                setAttempts(newAttempts);

                if (newAttempts >= 3) {
                    const blockTime = Date.now() + 5 * 60 * 1000; 
                    localStorage.setItem("blockedUntil", blockTime);
                    setBlockedUntil(blockTime);
                    startTimer(5 * 60 * 1000);
                    addBotMessage("Too many failed attempts. You are blocked for 5 minutes.");
                } else {
                    addBotMessage("Invalid employee code. Please try again.");
                }
            }
        } catch (error) {
            console.error("Error verifying employee code:", error);
            addBotMessage("An error occurred while verifying your employee code.");
        }
    };

    return (
        <div className="chatbot-container">
            <button className="chatbot-icon" onClick={toggleChat}>
                <ChatIcon size={24} />
            </button>
            {isOpen && (
                <div className="chatbot-window">
                    <div className="chatbot-header">
                        <span>Chat Support</span>
                        <button onClick={() => setIsOpen(false)}>×</button>
                    </div>
                    <div className="chatbot-body">
                        {messages.map((msg, index) => (
                            <div key={index} className={`chat-message ${msg.sender}`}>
                                {msg.text}
                            </div>
                        ))}
                        <div ref={chatEndRef}></div>
                    </div>
                    <div className="chatbot-footer">
                        <input
                            type="text"
                            placeholder="Type a message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                        />
                        <button onClick={sendMessage}>Send</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Chatbot;
